import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { donationsService } from '../services/api';
import { grantsService } from '../services/api';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { user } = useAuth();
  const [earningsData, setEarningsData] = useState(null);
  const [donationCountData, setDonationCountData] = useState(null);
  const [grantsData, setGrantsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const currentYear = new Date().getFullYear();

      // Fetch donations for current year
      const donationsRes = await donationsService.list({
        year: currentYear,
        limit: 99999,
      });

      // Fetch grants for current year
      const grantsRes = await grantsService.list({
        limit: 9999,
      });

      // Process earnings data by month
      const monthlyEarnings = new Array(12).fill(0);
      const monthlyDonations = new Array(12).fill(0);
      const monthlyGrants = new Array(12).fill(0);
      const monthlyDonationCount = new Array(12).fill(0);

      // Add donation amounts to monthly earnings
      if (donationsRes.data && Array.isArray(donationsRes.data.data || donationsRes.data)) {
        const donations = donationsRes.data.data || donationsRes.data;
        donations.forEach((donation) => {
          const donationDate = new Date(donation.donation_date);
          if (donationDate.getFullYear() === currentYear) {
            const month = donationDate.getMonth();
            const amount = parseFloat(donation.amount) || 0;
            monthlyDonations[month] += amount;
            monthlyEarnings[month] += amount;
            monthlyDonationCount[month] += 1;
          }
        });
      }

      // Add grant amounts to monthly earnings
      if (grantsRes.data && Array.isArray(grantsRes.data.data || grantsRes.data)) {
        const grants = grantsRes.data.data || grantsRes.data;
        grants.forEach((grant) => {
          const grantDate = new Date(grant.date_received || grant.created_at);
          if (grantDate.getFullYear() === currentYear && (grant.status === 'funded' || grant.status === 'Funded')) {
            const month = grantDate.getMonth();
            const amount = parseFloat(grant.amount_requested) || 0;
            monthlyGrants[month] += amount;
            monthlyEarnings[month] += amount;
          }
        });
      }

      // Process grants data
      let grantsApplied = 0;
      let grantsApproved = 0;
      let totalGrantAmount = 0;

      if (grantsRes.data && Array.isArray(grantsRes.data.data || grantsRes.data)) {
        const grants = grantsRes.data.data || grantsRes.data;
        grants.forEach((grant) => {
          const grantDate = new Date(grant.date_received || grant.created_at);
          if (grantDate.getFullYear() === currentYear) {
            grantsApplied += 1;
            if (grant.status === 'approved' || grant.status === 'Approved') {
              grantsApproved += 1;
            }
            if (grant.status === 'funded' || grant.status === 'Funded') {
              grantsApproved += 1;
              totalGrantAmount += parseFloat(grant.amount_requested) || 0;
            }
          }
        });
      }

      // Create grants summary chart data
      setGrantsData({
        applied: grantsApplied,
        approved: grantsApproved,
        totalAmount: totalGrantAmount,
        chartData: {
          labels: ['Applied', 'Approved'],
          datasets: [
            {
              label: 'Number of Grants',
              data: [grantsApplied, grantsApproved],
              backgroundColor: [
                'rgba(255, 159, 64, 0.7)',
                'rgba(75, 192, 75, 0.7)',
              ],
              borderColor: [
                'rgb(255, 159, 64)',
                'rgb(75, 192, 75)',
              ],
              borderWidth: 1,
            },
          ],
        },
      });

      // Create earnings chart data
      setEarningsData({
        labels: [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ],
        datasets: [
          {
            label: 'Donations',
            data: monthlyDonations,
            backgroundColor: 'rgba(153, 102, 255, 0.8)',
            borderColor: 'rgb(153, 102, 255)',
            borderWidth: 1,
            yAxisID: 'y',
          },
          {
            label: 'Grants',
            data: monthlyGrants,
            backgroundColor: 'rgba(75, 192, 192, 0.8)',
            borderColor: 'rgb(75, 192, 192)',
            borderWidth: 1,
            yAxisID: 'y1',
          },
        ],
      });

      // Create donation count chart data
      setDonationCountData({
        labels: [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ],
        datasets: [
          {
            label: 'Number of Donations',
            data: monthlyDonationCount,
            backgroundColor: 'rgba(153, 102, 255, 0.7)',
            borderColor: 'rgb(153, 102, 255)',
            borderWidth: 1,
          },
        ],
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
        title: {
          display: true,
          text: 'Grants Applied',
        },
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        title: {
          display: true,
          text: 'Grants Funded',
        },
      },
    },
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Green Hill Metrics</h1>
        <div className="header-info">
          {user?.role === 'admin' && (
            <span className="role-badge admin">Admin</span>
          )}
        </div>
      </div>

      <div className="dashboard-content">
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Loading dashboard data...</div>
        ) : (
          <>
            <div className="charts-grid">
              <div className="chart-container">
                <h2>Total Earnings (Grants + Donations) - {new Date().getFullYear()}</h2>
                {earningsData && (
                  <Bar
                    data={earningsData}
                    options={{
                      ...chartOptions,
                      scales: {
                        y: {
                          ...chartOptions.scales.y,
                          title: {
                            display: true,
                            text: 'Total Earnings ($)',
                          },
                        },
                        y1: {
                          ...chartOptions.scales.y1,
                          title: {
                            display: true,
                            text: 'Total Funded Grants ($)',
                          },
                        }
                      }
                    }}
                  />
                )}
              </div>

              <div className="chart-container">
                <h2>Number of Donations - {new Date().getFullYear()}</h2>
                {donationCountData && (
                  <Bar
                    data={donationCountData}
                    options={{
                      ...chartOptions,
                      scales: {}
                    }}
                  />
                )}
              </div>

              <div className="chart-container">
                <h2>Grants Overview - {new Date().getFullYear()}</h2>
                {grantsData && (
                  <div className="grants-overview">
                    <Bar
                      data={grantsData.chartData}
                      options={chartOptions}
                    />
                    <div className="grants-summary">
                      <div className="summary-card">
                        <h3>Total Grants Applied</h3>
                        <p className="summary-value">{grantsData.applied}</p>
                      </div>
                      <div className="summary-card">
                        <h3>Grants Approved</h3>
                        <p className="summary-value">{grantsData.approved}</p>
                      </div>
                      <div className="summary-card highlight">
                        <h3>Total Grants Amount</h3>
                        <p className="summary-value">${grantsData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
